<template>
  <div class="onboard-overlay" @click.self="$emit('close')">
    <div class="onboard-modal">

      <!-- Header -->
      <div class="onboard-modal__header">
        <h2 class="onboard-modal__title">Onboard a New Client</h2>
        <button class="onboard-modal__close" @click="$emit('close')">
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <!-- Step Indicator -->
      <div v-if="!editMode" class="onboard-modal__steps">
        <span :class="['onboard-step', step === 1 && 'onboard-step--active', step > 1 && 'onboard-step--done']">
          <i v-if="step > 1" class="mdi mdi-check-circle"></i>1. Company
        </span>
        <span class="onboard-step__sep"></span>
        <span :class="['onboard-step', step === 2 && 'onboard-step--active', step > 2 && 'onboard-step--done']">
          <i v-if="step > 2" class="mdi mdi-check-circle"></i>2. Users
        </span>
        <span class="onboard-step__sep"></span>
        <span :class="['onboard-step', step === 3 && 'onboard-step--active']">
          3. Org Structure
        </span>
      </div>

      <!-- ── Step 1: Company Form ── -->
      <div v-if="step === 1" class="onboard-modal__body">
        <div class="onboard-form-grid">
          <div class="onboard-form-row onboard-form-row--half">
            <div class="onboard-field">
              <label>Company Name <span class="req">*</span></label>
              <input v-model="form.name" placeholder="Acme Corp" />
            </div>
            <div class="onboard-field">
              <label>Handle(s) <span class="req">*</span></label>
              <input v-model="form.handle" placeholder="acme, acme-corp" />
              <small class="onboard-field__hint">Comma-separated email domain(s) used to match users on signup.</small>
            </div>
          </div>

          <div class="onboard-form-row onboard-form-row--half">
            <div class="onboard-field">
              <label>Phone</label>
              <input v-model="form.phone" placeholder="555-555-5555" />
            </div>
            <div class="onboard-field">
              <label>Address</label>
              <input v-model="form.address1" placeholder="123 Main St" />
            </div>
          </div>

          <div class="onboard-form-row onboard-form-row--thirds">
            <div class="onboard-field">
              <label>City</label>
              <input v-model="form.city" placeholder="New York" />
            </div>
            <div class="onboard-field">
              <label>State</label>
              <input v-model="form.state" placeholder="NY" />
            </div>
            <div class="onboard-field">
              <label>Zip</label>
              <input v-model="form.zip" placeholder="10001" />
            </div>
          </div>

          <div class="onboard-field">
            <label>Description</label>
            <textarea v-model="form.description" placeholder="Brief description..." rows="2" />
          </div>
        </div>

        <div class="onboard-section-title">PSA System</div>
        <div class="onboard-field onboard-field--psa">
          <select v-model="psaType">
            <option value="connectwise">ConnectWise</option>
            <option value="kaseya">Kaseya (Autotask + IT Glue)</option>
          </select>
        </div>

        <template v-if="psaType === 'connectwise'">
          <div class="onboard-form-grid">
            <div class="onboard-form-row onboard-form-row--half">
              <div class="onboard-field">
                <label>API Base URL</label>
                <input v-model="form.cwApiBaseUrl" placeholder="https://connect.example.com" />
              </div>
              <div class="onboard-field">
                <label>Client ID</label>
                <input v-model="form.cwClientId" placeholder="..." />
              </div>
            </div>
            <div class="onboard-form-row onboard-form-row--thirds">
              <div class="onboard-field">
                <label>Company ID</label>
                <input v-model="form.cwCompanyId" placeholder="acme_corp" />
              </div>
              <div class="onboard-field">
                <label>Public Key</label>
                <input
                  v-model="form.cwPublicKey"
                  :placeholder="cwCredentialsConfigured ? '(stored — leave blank to keep)' : '...'"
                />
              </div>
              <div class="onboard-field">
                <label>Private Key</label>
                <input
                  v-model="form.cwPrivateKey"
                  type="password"
                  :placeholder="cwCredentialsConfigured ? '(stored — leave blank to keep)' : '...'"
                />
              </div>
            </div>
          </div>

          <label class="onboard-toggle">
            <input type="checkbox" v-model="form.dailyWorkSync" />
            Enable nightly ConnectWise sync
          </label>
        </template>

        <template v-else-if="psaType === 'kaseya'">
          <div class="onboard-form-grid">
            <div class="onboard-form-row onboard-form-row--half">
              <div class="onboard-field">
                <label>Autotask API Base URL</label>
                <input v-model="form.atApiUrl" placeholder="(leave blank to auto-detect)" />
              </div>
              <div class="onboard-field">
                <label>Autotask API User</label>
                <input v-model="form.atApiUser" placeholder="integration@example.com" />
              </div>
            </div>
            <div class="onboard-form-row onboard-form-row--half">
              <div class="onboard-field">
                <label>Autotask API Secret</label>
                <input
                  v-model="form.atApiSecret"
                  type="password"
                  :placeholder="atCredentialsConfigured ? '(stored — leave blank to keep)' : '...'"
                />
              </div>
              <div class="onboard-field">
                <label>Autotask Tracking Identifier</label>
                <input
                  v-model="form.atTrackingIdentifier"
                  type="password"
                  :placeholder="atCredentialsConfigured ? '(stored — leave blank to keep)' : 'API Integration Code'"
                />
              </div>
            </div>
            <div class="onboard-field">
              <label>IT Glue API Key</label>
              <input
                v-model="form.itGlueApiKey"
                type="password"
                :placeholder="itGlueCredentialsConfigured ? '(stored — leave blank to keep)' : '...'"
              />
            </div>
          </div>
          <p class="onboard-intro onboard-intro--sm">
            IT Glue account context syncs nightly once a key is saved. Autotask ticket sync
            isn't built yet — Nightly Ticket Sync stays ConnectWise-only for now.
          </p>
        </template>

        <div v-if="testResult" :class="['onboard-status', testResult.ok ? 'onboard-status--ok' : 'onboard-status--err']">
          <i :class="['mdi', testResult.ok ? 'mdi-check-circle' : 'mdi-alert-circle']"></i>
          {{ testResult.message }}
        </div>

        <div v-if="formError" class="onboard-status onboard-status--err">
          <i class="mdi mdi-alert-circle"></i> {{ formError }}
        </div>

        <div class="onboard-modal__actions">
          <button
            v-if="psaType === 'connectwise'"
            class="onboard-btn onboard-btn--secondary"
            :disabled="!cwCredentialsComplete || testingConnection"
            @click="testConnection"
          >
            {{ testingConnection ? 'Testing...' : 'Test Connection' }}
          </button>
          <button
            class="onboard-btn onboard-btn--primary"
            :disabled="!formValid || saving"
            @click="saveCompany"
          >
            {{ saving ? 'Saving...' : editMode ? 'Save Changes' : 'Create & Continue' }}
          </button>
        </div>
      </div>

      <!-- ── Step 2: Contacts ── -->
      <div v-if="step === 2" class="onboard-modal__body">
        <p class="onboard-intro">
          Load active {{ psaType === 'kaseya' ? 'resources from Autotask' : 'contacts from ConnectWise' }},
          select which to create as users, then import them.
          New users will be able to log in via magic link when they're ready.
        </p>

        <div v-if="!contactsLoaded" class="onboard-modal__actions">
          <button class="onboard-btn onboard-btn--primary" :disabled="loadingContacts" @click="loadContacts">
            {{ loadingContacts ? 'Loading...' : `Load Contacts from ${psaType === 'kaseya' ? 'Autotask' : 'ConnectWise'}` }}
          </button>
          <button class="onboard-btn onboard-btn--ghost" @click="goToStep3([])">Skip — add users later</button>
        </div>

        <div v-else>
          <p class="onboard-intro onboard-intro--sm">
            <template v-if="handleDomains.length && filteredContacts.length < contacts.length">
              Showing {{ filteredContacts.length }} of {{ contacts.length }} contacts matching
              <strong>{{ handleDomains.join(', ') }}</strong>.
            </template>
            <template v-else>
              {{ contacts.length }} active contacts with email found.
            </template>
            Select which to create as users.
          </p>

          <div class="onboard-contact-list">
            <label v-for="c in filteredContacts" :key="c.id" class="onboard-contact-row">
              <input type="checkbox" :value="c.id" v-model="selectedContactIds" />
              <span class="contact-name">{{ c.firstName }} {{ c.lastName }}</span>
              <span class="contact-email">{{ c.email }}</span>
            </label>
          </div>

          <div v-if="importResult" class="onboard-status onboard-status--ok">
            <i class="mdi mdi-check-circle"></i>
            {{ importResult.created.length }} users created
            <span v-if="importResult.skipped.length">, {{ importResult.skipped.length }} skipped (already exist)</span>.
          </div>

          <div v-if="importError" class="onboard-status onboard-status--err">
            <i class="mdi mdi-alert-circle"></i> {{ importError }}
          </div>

          <div class="onboard-modal__actions">
            <button class="onboard-btn onboard-btn--ghost" @click="toggleAllContacts">
              {{ filteredContacts.every(c => selectedContactIds.includes(c.id)) ? 'Deselect All' : 'Select All' }}
            </button>
            <button
              v-if="!importResult"
              class="onboard-btn onboard-btn--secondary"
              :disabled="!selectedContactIds.length || importingContacts"
              @click="importContacts"
            >
              {{ importingContacts ? 'Creating...' : `Create ${selectedContactIds.length} User${selectedContactIds.length !== 1 ? 's' : ''}` }}
            </button>
            <button class="onboard-btn onboard-btn--primary" @click="goToStep3(importResult ? importResult.created : [])">
              {{ importResult ? 'Next: Org Structure' : 'Skip — set org later' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ── Step 3: Org Structure ── -->
      <div v-if="step === 3" class="onboard-modal__body">
        <p class="onboard-intro">
          Drag users to assign their role. You can always update this later from the tenant's roster.
        </p>

        <div class="org-board">
          <!-- Unassigned pool -->
          <div
            class="org-zone org-zone--pool"
            @dragover.prevent
            @drop="onDropUnassigned"
          >
            <div class="org-zone__title">Unassigned</div>
            <div
              v-for="user in orgUnassigned"
              :key="user._id"
              class="org-card"
              draggable="true"
              @dragstart="onDragStart(user, { from: 'unassigned' })"
            >
              <i class="mdi mdi-account"></i>
              <span>{{ user.firstName }} {{ user.lastName }}</span>
              <span class="org-card__email">{{ user.email }}</span>
            </div>
            <p v-if="!orgUnassigned.length" class="org-zone__empty">All assigned</p>
          </div>

          <!-- Right side -->
          <div class="org-board__right">
            <!-- Admin zone -->
            <div
              class="org-zone org-zone--admin"
              @dragover.prevent
              @drop="onDropAdmin"
            >
              <div class="org-zone__title">Company Admins <span class="org-zone__hint">(at least 1 — the first is the tenant owner, the only admin who can grant or remove admin)</span></div>
              <div
                v-for="(admin, idx) in orgAdmins"
                :key="admin._id"
                class="org-card org-card--admin"
                draggable="true"
                @dragstart="onDragStart(admin, { from: 'admin', managerIdx: idx })"
              >
                <i class="mdi mdi-shield-crown"></i>
                <span>{{ admin.firstName }} {{ admin.lastName }}</span>
                <span v-if="idx === 0" class="org-card__badge">tenant owner</span>
                <button
                  v-else-if="createdCompanyId"
                  type="button"
                  class="org-card__action"
                  :disabled="Boolean(transferringOwnerId) || admin.isAccountActivated === false"
                  :title="admin.isAccountActivated === false ? 'Only an activated user can be the tenant owner.' : 'Make this admin the tenant owner now'"
                  @click.stop="makeOwner(admin)"
                >
                  {{ transferringOwnerId === String(admin._id) ? 'Transferring...' : 'Make owner' }}
                </button>
              </div>
              <div v-if="ownerError" class="onboard-status onboard-status--err">
                <i class="mdi mdi-alert-circle"></i> {{ ownerError }}
              </div>
              <p class="org-zone__hint-drop">
                {{ orgAdmins.length ? 'Drop another user here to add an admin' : 'Drop a user here to make them admin' }}
              </p>
            </div>

            <!-- Direct reports nested under admin -->
            <div class="org-admin-reports">
              <div class="org-admin-reports__connector"></div>
              <div
                class="org-zone org-zone--managers"
                @dragover.prevent
                @drop="onDropManager"
              >
                <div class="org-zone__title">
                  Direct Reports of Admin
                  <span class="org-zone__hint">(drop any user here)</span>
                </div>
                <div
                  v-for="(entry, idx) in orgManagers"
                  :key="entry.user._id"
                  class="org-manager"
                >
                  <div
                    class="org-card"
                    :class="entry.reports.length ? 'org-card--manager' : 'org-card--report'"
                    draggable="true"
                    @dragstart="onDragStart(entry.user, { from: 'manager', managerIdx: idx })"
                  >
                    <i :class="['mdi', entry.reports.length ? 'mdi-account-tie' : 'mdi-account']"></i>
                    <span>{{ entry.user.firstName }} {{ entry.user.lastName }}</span>
                    <span v-if="entry.reports.length" class="org-card__badge">manager</span>
                    <span v-if="orgAdmins.length > 1 && reportsToAdmin(entry)" class="org-card__email">
                      reports to {{ reportsToAdmin(entry).firstName }}
                    </span>
                  </div>
                  <div
                    class="org-manager__reports"
                    @dragover.prevent
                    @drop="onDropReport($event, idx)"
                  >
                    <p class="org-zone__title org-zone__title--sm">
                      Reports to {{ entry.user.firstName }}
                      <span class="org-zone__hint">(drop to add — makes them a manager)</span>
                    </p>
                    <div
                      v-for="report in entry.reports"
                      :key="report._id"
                      class="org-card org-card--report"
                      draggable="true"
                      @dragstart="onDragStart(report, { from: 'report', managerIdx: idx })"
                    >
                      <i class="mdi mdi-account-arrow-up"></i>
                      <span>{{ report.firstName }} {{ report.lastName }}</span>
                    </div>
                    <p v-if="!entry.reports.length" class="org-zone__hint-drop">Drop users here to make {{ entry.user.firstName }} a manager</p>
                  </div>
                </div>
                <p v-if="!orgManagers.length" class="org-zone__hint-drop">Drop any user here to add them as a direct report of admin</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="orgSaveError" class="onboard-status onboard-status--err">
          <i class="mdi mdi-alert-circle"></i> {{ orgSaveError }}
        </div>

        <div v-if="orgSkipped.length" class="onboard-status onboard-status--err">
          <i class="mdi mdi-alert-circle"></i>
          Saved. These people kept their current manager, because that manager isn't shown in this chart
          (for example, a deeper level of the org). Move them from their current manager's team instead:
          <ul class="org-skipped">
            <li v-for="item in orgSkipped" :key="item.userId">{{ item.name || item.email || item.userId }}</li>
          </ul>
        </div>

        <div class="onboard-modal__actions">
          <button class="onboard-btn onboard-btn--ghost" @click="finish">Skip &amp; Finish</button>
          <button v-if="orgSkipped.length" class="onboard-btn onboard-btn--primary" @click="finish">Done</button>
          <button v-else class="onboard-btn onboard-btn--primary" :disabled="savingOrg" @click="saveOrgStructure">
            {{ savingOrg ? 'Saving...' : 'Save &amp; Finish' }}
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'OnboardClientModal',
  props: {
    authHeader: {
      type: Object,
      required: true,
    },
    existingCompanyId: {
      type: String,
      default: null,
    },
    initialStep: {
      type: Number,
      default: 1,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    companyHandles: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'done'],
  data() {
    return {
      step: 1,
      form: {
        name: '',
        handle: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        description: '',
        cwApiBaseUrl: '',
        cwClientId: '',
        cwCompanyId: '',
        cwPublicKey: '',
        cwPrivateKey: '',
        dailyWorkSync: false,
        atApiUrl: '',
        atApiUser: '',
        atApiSecret: '',
        atTrackingIdentifier: '',
        itGlueApiKey: '',
      },
      psaType: 'connectwise',
      cwCredentialsConfigured: false,
      atCredentialsConfigured: false,
      itGlueCredentialsConfigured: false,
      saving: false,
      formError: null,
      testingConnection: false,
      testResult: null,
      createdCompanyId: null,

      // Step 2
      loadingContacts: false,
      contactsLoaded: false,
      contacts: [],
      selectedContactIds: [],
      importingContacts: false,
      importResult: null,
      importError: null,

      // Step 3
      orgAdmins: [],
      orgManagers: [],
      orgUnassigned: [],
      // Ids the superuser explicitly dragged to Unassigned (their line is cleared on save). People
      // who start in Unassigned (e.g. deeper org levels) are NOT in here and keep their lines.
      orgClearedIds: [],
      orgSkipped: [],
      draggedUser: null,
      draggedFrom: null,
      draggedManagerIdx: null,
      savingOrg: false,
      orgSaveError: null,
      // spec-tenant-owner: super-user "Make owner" (PATCH .../owner) in flight / its error.
      transferringOwnerId: null,
      ownerError: null,
      // The company's tenant owner as loaded (or after "Make owner"): { _id, name } or null. A save
      // whose first admin differs from it changes the owner, so it asks first.
      currentOwner: null,
    };
  },
  async mounted() {
    if (this.existingCompanyId) {
      this.createdCompanyId = this.existingCompanyId;
      if (this.editMode) {
        await this.loadCompanyForEdit();
      } else if (this.initialStep === 3) {
        await this.loadExistingUsersForOrg();
      } else {
        this.step = this.initialStep;
      }
    }
  },
  computed: {
    cwCredentialsComplete() {
      // cwPublicKey/cwPrivateKey are never returned by the API (stripped server-side) — treat
      // them as set if the backend confirms credentials are already configured, same as
      // Company.vue's hasRequiredCwFields.
      return !!(
        this.form.cwApiBaseUrl &&
        this.form.cwClientId &&
        this.form.cwCompanyId &&
        ((this.form.cwPublicKey && this.form.cwPrivateKey) || this.cwCredentialsConfigured)
      );
    },
    formValid() {
      const handles = this.form.handle.split(',').map(h => h.trim()).filter(Boolean);
      return !!(this.form.name && handles.length);
    },
    handleDomains() {
      if (this.companyHandles.length) {
        return this.companyHandles.map(h => h.trim().toLowerCase()).filter(Boolean);
      }
      return this.form.handle
        .split(',')
        .map(h => h.trim().toLowerCase())
        .filter(Boolean);
    },
    filteredContacts() {
      if (!this.handleDomains.length) return this.contacts;
      return this.contacts.filter(c => {
        const domain = (c.email || '').toLowerCase().split('@')[1] || '';
        return this.handleDomains.some(h => domain === h || domain.endsWith('.' + h));
      });
    },
    apiBase() {
      return process.env.VUE_APP_API_URL;
    },
  },
  methods: {
    async loadCompanyForEdit() {
      try {
        const res = await axios.get(
          `${this.apiBase}/superuser/companies/${this.existingCompanyId}`,
          { headers: this.authHeader }
        );
        const c = res.data;
        this.form.name = c.name || '';
        this.form.handle = Array.isArray(c.handle) ? c.handle.join(', ') : (c.handle || '');
        this.form.phone = c.phone || '';
        this.form.address1 = c.address1 || '';
        this.form.address2 = c.address2 || '';
        this.form.city = c.city || '';
        this.form.state = c.state || '';
        this.form.zip = c.zip || '';
        this.form.description = c.description || '';
        this.form.cwApiBaseUrl = c.cwApiBaseUrl || '';
        this.form.cwClientId = c.cwClientId || '';
        this.form.cwCompanyId = c.cwCompanyId || '';
        // cwPublicKey/cwPrivateKey and every Autotask/IT Glue secret below are stripped by the
        // backend (sanitizeCompanyForClient) — never pre-filled, left blank to be re-entered.
        // cwPublicKey/atApiUser/atApiUrl are NOT secrets and would normally be safe to pre-fill,
        // but cwPublicKey is still stripped (see company.prisma.service.js's
        // CLIENT_HIDDEN_FIELDS) for consistency with Company.vue's handling of it.
        this.form.cwPublicKey = '';
        this.form.cwPrivateKey = '';
        this.form.atApiUrl = c.atApiUrl || '';
        this.form.atApiUser = c.atApiUser || '';
        this.form.atApiSecret = '';
        this.form.atTrackingIdentifier = '';
        this.form.itGlueApiKey = '';
        this.form.dailyWorkSync = !!c.dailyWorkSync;
        this.cwCredentialsConfigured = !!c.cwCredentialsConfigured;
        this.atCredentialsConfigured = !!c.atCredentialsConfigured;
        this.itGlueCredentialsConfigured = !!c.itGlueCredentialsConfigured;
        this.psaType = c.activeTicketPsa === 'autotask' ? 'kaseya' : 'connectwise';
        this.step = 1;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load company details.', type: 'is-danger' });
      }
    },

    async loadExistingUsersForOrg() {
      try {
        const res = await axios.get(
          `${this.apiBase}/superuser/companies/${this.createdCompanyId}/users`,
          { headers: this.authHeader }
        );
        this._applyOrgState(res.data || []);
        this.step = 3;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load existing users.', type: 'is-danger' });
      }
    },

    async testConnection() {
      this.testingConnection = true;
      this.testResult = null;
      try {
        await axios.post(
          `${this.apiBase}/superuser/test-cw`,
          {
            companyId: this.form.cwCompanyId,
            publicKey: this.form.cwPublicKey,
            privateKey: this.form.cwPrivateKey,
            clientId: this.form.cwClientId,
            baseUrl: this.form.cwApiBaseUrl,
          },
          { headers: this.authHeader }
        );
        this.testResult = { ok: true, message: 'Connection successful.' };
      } catch (err) {
        const msg = err?.response?.data?.message || 'Connection failed. Check your credentials.';
        this.testResult = { ok: false, message: msg };
      } finally {
        this.testingConnection = false;
      }
    },

    async saveCompany() {
      this.saving = true;
      this.formError = null;
      try {
        // Secret-like fields (cwPublicKey/cwPrivateKey/atApiSecret/atTrackingIdentifier/
        // itGlueApiKey) are deliberately excluded from the blanket ...this.form spread and only
        // included when non-blank. Regression: loadCompanyForEdit always leaves these blank
        // (the backend never returns them), so spreading them unconditionally here silently
        // overwrote every stored secret with an empty string on every single "Save Changes" —
        // including edits that had nothing to do with credentials.
        const { cwPublicKey, cwPrivateKey, atApiSecret, atTrackingIdentifier, itGlueApiKey, ...restOfForm } = this.form;
        const payload = {
          ...restOfForm,
          ...(cwPublicKey ? { cwPublicKey } : {}),
          ...(cwPrivateKey ? { cwPrivateKey } : {}),
          ...(atApiSecret ? { atApiSecret } : {}),
          ...(atTrackingIdentifier ? { atTrackingIdentifier } : {}),
          ...(itGlueApiKey ? { itGlueApiKey } : {}),
          activeTicketPsa: this.psaType === 'kaseya' ? 'autotask' : 'connectwise',
          handle: this.form.handle.split(',').map(h => h.trim().toLowerCase()).filter(Boolean),
        };
        if (this.editMode) {
          await axios.patch(
            `${this.apiBase}/superuser/companies/${this.existingCompanyId}`,
            payload,
            { headers: this.authHeader }
          );
          this.$buefy.toast.open({ message: 'Company updated.', type: 'is-success' });
          this.$emit('done', { companyId: this.existingCompanyId });
        } else {
          const res = await axios.post(
            `${this.apiBase}/superuser/companies`,
            payload,
            { headers: this.authHeader }
          );
          this.createdCompanyId = res.data._id;
          this.step = 2;
        }
      } catch (err) {
        this.formError = err?.response?.data?.message || `Failed to ${this.editMode ? 'update' : 'create'} company.`;
      } finally {
        this.saving = false;
      }
    },

    async loadContacts() {
      this.loadingContacts = true;
      const endpoint = this.psaType === 'kaseya' ? 'at-contacts' : 'cw-contacts';
      try {
        const res = await axios.get(
          `${this.apiBase}/superuser/companies/${this.createdCompanyId}/${endpoint}`,
          { headers: this.authHeader }
        );
        this.contacts = res.data.contacts || [];
        this.selectedContactIds = this.filteredContacts.map(c => c.id);
        this.contactsLoaded = true;
      } catch (err) {
        const msg = err?.response?.data?.message || 'Failed to load contacts.';
        this.$buefy.toast.open({ message: msg, type: 'is-danger' });
      } finally {
        this.loadingContacts = false;
      }
    },

    toggleAllContacts() {
      const filteredIds = this.filteredContacts.map(c => c.id);
      const allSelected = filteredIds.every(id => this.selectedContactIds.includes(id));
      if (allSelected) {
        this.selectedContactIds = this.selectedContactIds.filter(id => !filteredIds.includes(id));
      } else {
        const combined = new Set([...this.selectedContactIds, ...filteredIds]);
        this.selectedContactIds = [...combined];
      }
    },

    async importContacts() {
      this.importingContacts = true;
      this.importError = null;
      const selected = this.contacts.filter(c => this.selectedContactIds.includes(c.id));
      try {
        const res = await axios.post(
          `${this.apiBase}/superuser/companies/${this.createdCompanyId}/import-contacts`,
          { contacts: selected },
          { headers: this.authHeader }
        );
        this.importResult = res.data;
      } catch (err) {
        this.importError = err?.response?.data?.message || 'Failed to import contacts.';
      } finally {
        this.importingContacts = false;
      }
    },

    async goToStep3(newUsers) {
      this.orgAdmins = [];
      this.orgManagers = [];
      this.orgUnassigned = [];
      this.orgClearedIds = [];
      this.orgSkipped = [];

      if (this.createdCompanyId) {
        try {
          const res = await axios.get(
            `${this.apiBase}/superuser/companies/${this.createdCompanyId}/users`,
            { headers: this.authHeader }
          );
          const existing = res.data || [];
          this._applyOrgState(existing, newUsers);
        } catch {
          this.orgUnassigned = newUsers.map(u => ({ ...u }));
        }
      } else {
        this.orgUnassigned = newUsers.map(u => ({ ...u }));
      }

      this.step = 3;
    },

    _applyOrgState(existingUsers, newUsers = []) {
      this.orgAdmins = [];
      this.orgManagers = [];

      const assignedIds = new Set();
      // Multi-admin (spec-multi-admin-per-company): every isAdmin user is listed, the current
      // tenant owner first (spec-tenant-owner: the save makes the first listed admin the owner).
      const isOwnerRow = u => Boolean(u.isTenantOwner ?? u.isPrimaryContact);
      const adminUsers = existingUsers
        .filter(u => u.isAdmin)
        .sort((a, b) => Number(isOwnerRow(b)) - Number(isOwnerRow(a)));

      const ownerUser = adminUsers.find(isOwnerRow);
      this.currentOwner = ownerUser
        ? { _id: ownerUser._id, name: `${ownerUser.firstName || ''} ${ownerUser.lastName || ''}`.trim() || ownerUser.email }
        : null;

      for (const adminUser of adminUsers) {
        this.orgAdmins.push({
          _id: adminUser._id,
          firstName: adminUser.firstName,
          lastName: adminUser.lastName,
          email: adminUser.email,
          isAccountActivated: adminUser.isAccountActivated,
        });
        assignedIds.add(String(adminUser._id));
      }

      const adminIds = new Set(adminUsers.map(u => String(u._id)));
      const directReports = existingUsers.filter(u => adminIds.has(String(u.manager)) && !u.isAdmin);

      for (const dr of directReports) {
        const subReports = existingUsers
          .filter(u => String(u.manager) === String(dr._id))
          .map(u => ({ _id: u._id, firstName: u.firstName, lastName: u.lastName, email: u.email }));
        this.orgManagers.push({
          user: { _id: dr._id, firstName: dr.firstName, lastName: dr.lastName, email: dr.email },
          reports: subReports,
          // Which admin this person reports to — kept so each admin's own direct reports
          // round-trip unchanged on save.
          adminId: dr.manager,
        });
        assignedIds.add(String(dr._id));
        for (const r of subReports) assignedIds.add(String(r._id));
      }

      const existingIds = new Set(existingUsers.map(u => String(u._id)));
      const unassignedExisting = existingUsers
        .filter(u => !assignedIds.has(String(u._id)))
        .map(u => ({ _id: u._id, firstName: u.firstName, lastName: u.lastName, email: u.email }));
      const unassignedNew = newUsers
        .filter(u => !existingIds.has(String(u._id)))
        .map(u => ({ ...u }));

      this.orgUnassigned = [...unassignedExisting, ...unassignedNew];
    },

    // ── Drag-and-drop helpers ──

    onDragStart(user, { from, managerIdx = null } = {}) {
      this.draggedUser = user;
      this.draggedFrom = from;
      this.draggedManagerIdx = managerIdx;
    },

    removeFromSource() {
      const user = this.draggedUser;
      const from = this.draggedFrom;
      const idx = this.draggedManagerIdx;

      if (from === 'unassigned') {
        this.orgUnassigned = this.orgUnassigned.filter(u => u._id !== user._id);
      } else if (from === 'admin') {
        this.orgAdmins = this.orgAdmins.filter(u => u._id !== user._id);
      } else if (from === 'manager') {
        // Also move the manager's reports back to unassigned (an explicit clear for each)
        const entry = this.orgManagers[idx];
        if (entry) {
          this.orgUnassigned.push(...entry.reports);
          entry.reports.forEach(r => this.markCleared(r._id));
        }
        this.orgManagers.splice(idx, 1);
      } else if (from === 'report') {
        this.orgManagers[idx].reports = this.orgManagers[idx].reports.filter(
          u => u._id !== user._id
        );
      }
    },

    markCleared(id) {
      const key = String(id);
      if (!this.orgClearedIds.includes(key)) this.orgClearedIds.push(key);
    },

    unmarkCleared(id) {
      this.orgClearedIds = this.orgClearedIds.filter(cleared => cleared !== String(id));
    },

    onDropAdmin() {
      if (!this.draggedUser) return;
      // Dropping an existing admin back onto the admin zone is a no-op.
      if (this.draggedFrom === 'admin') {
        this.draggedUser = null;
        return;
      }
      const user = { ...this.draggedUser };
      this.removeFromSource();
      this.unmarkCleared(user._id);
      this.orgAdmins.push(user);
      this.draggedUser = null;
    },

    onDropManager() {
      if (!this.draggedUser) return;
      const user = { ...this.draggedUser };
      this.removeFromSource();
      this.unmarkCleared(user._id);
      // New direct reports go to the tenant owner; existing ones keep their own admin.
      this.orgManagers.push({ user, reports: [], adminId: this.orgAdmins[0]?._id || null });
      this.draggedUser = null;
    },

    // The admin an entry reports to, for display: its own admin if still listed, else the
    // tenant owner (the backend applies the same fallback).
    reportsToAdmin(entry) {
      return this.orgAdmins.find(a => String(a._id) === String(entry.adminId)) || this.orgAdmins[0] || null;
    },

    onDropReport(event, managerIdx) {
      if (!this.draggedUser) return;
      const user = { ...this.draggedUser };
      this.removeFromSource();
      this.unmarkCleared(user._id);
      this.orgManagers[managerIdx].reports.push(user);
      this.draggedUser = null;
    },

    onDropUnassigned() {
      if (!this.draggedUser) return;
      const user = { ...this.draggedUser };
      this.removeFromSource();
      if (this.draggedFrom !== 'unassigned') this.markCleared(user._id);
      this.orgUnassigned.push(user);
      this.draggedUser = null;
    },

    // spec-tenant-owner: transfer ownership right away (super users only; the server requires an
    // activated user of this company, makes them admin if needed and keeps the previous owner an
    // admin). On success the new owner moves to the front, so a later save keeps them as owner.
    async makeOwner(admin) {
      if (!this.createdCompanyId || this.transferringOwnerId) return;
      this.ownerError = null;
      this.transferringOwnerId = String(admin._id);
      try {
        await axios.patch(
          `${this.apiBase}/superuser/companies/${this.createdCompanyId}/owner`,
          { userId: admin._id },
          { headers: this.authHeader }
        );
        const idx = this.orgAdmins.findIndex(a => String(a._id) === String(admin._id));
        if (idx > 0) {
          const [owner] = this.orgAdmins.splice(idx, 1);
          this.orgAdmins.unshift(owner);
        }
        this.currentOwner = { _id: admin._id, name: this.adminDisplayName(admin) };
      } catch (err) {
        this.ownerError = err?.response?.data?.message || 'Failed to transfer tenant ownership.';
      } finally {
        this.transferringOwnerId = null;
      }
    },

    adminDisplayName(user) {
      return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'this user';
    },

    // spec-tenant-owner review: the save makes the first listed admin the owner. If that is not
    // the current owner, confirm first. Reordering keeps the previous owner an admin; only
    // dragging them out of the admin zone (an explicit removal) takes admin away.
    confirmOwnerChange() {
      const newOwner = this.orgAdmins[0];
      if (!this.currentOwner || !newOwner || String(newOwner._id) === String(this.currentOwner._id)) return true;
      const stillAdmin = this.orgAdmins.some(a => String(a._id) === String(this.currentOwner._id));
      const consequence = stillAdmin
        ? `${this.currentOwner.name} stays an admin.`
        : 'The previous owner will lose admin.';
      return window.confirm(
        `This changes the tenant owner from ${this.currentOwner.name} to ${this.adminDisplayName(newOwner)}. ${consequence}`
      );
    },

    async saveOrgStructure() {
      this.orgSaveError = null;
      if (!this.orgAdmins.length) {
        this.orgSaveError = 'Add at least one company admin before saving.';
        return;
      }
      if (!this.confirmOwnerChange()) return;
      this.savingOrg = true;
      this.orgSkipped = [];
      const stillUnassigned = new Set(this.orgUnassigned.map(u => String(u._id)));
      const unassignedIds = this.orgClearedIds.filter(id => stillUnassigned.has(id));
      const reportingStructure = this.orgManagers.map(entry => ({
        managerId: entry.user._id,
        reportIds: entry.reports.map(r => r._id),
        adminId: this.reportsToAdmin(entry)?._id || null,
      }));
      try {
        const response = await axios.patch(
          `${this.apiBase}/superuser/companies/${this.createdCompanyId}/org-structure`,
          {
            // adminIds[] sets isAdmin for exactly these users; ownerId (first listed) is the
            // tenant owner and must be one of them. adminId is kept for older backends.
            adminIds: this.orgAdmins.map(u => u._id),
            ownerId: this.orgAdmins[0]?._id || null,
            adminId: this.orgAdmins[0]?._id || null,
            reportingStructure,
            // Only people explicitly dragged to Unassigned lose their manager.
            unassignedIds,
          },
          { headers: this.authHeader }
        );
        const skipped = Array.isArray(response?.data?.skippedPlacements) ? response.data.skippedPlacements : [];
        if (skipped.length) {
          this.orgSkipped = skipped;
          return;
        }
        this.finish();
      } catch (err) {
        this.orgSaveError = err?.response?.data?.message || 'Failed to save org structure.';
      } finally {
        this.savingOrg = false;
      }
    },

    finish() {
      this.$emit('done', { companyId: this.createdCompanyId });
    },
  },
};
</script>

<style lang="scss" scoped>
.onboard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
}

.onboard-modal {
  background: $color-surface;
  border-radius: 1rem;
  width: 100%;
  max-width: 780px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.onboard-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem 1rem;
  border-bottom: 1px solid $color-border;
}

.onboard-modal__title {
  font-size: 1.35rem;
  font-weight: 800;
  color: $color-text;
  margin: 0;
}

.onboard-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.4rem;
  color: $color-text-muted;
  padding: 0.25rem;

  &:hover { color: $color-text; }
}

.onboard-modal__steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  background: $color-surface-alt;
  border-bottom: 1px solid $color-border;
}

.onboard-step {
  font-size: 0.85rem;
  font-weight: 600;
  color: $color-text-muted;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &--active { color: $color-primary; }
  &--done { color: $color-success; }
}

.onboard-step__sep {
  flex: 1;
  height: 1px;
  background: $color-border;
  min-width: 1.5rem;
}

.onboard-modal__body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.onboard-intro {
  font-size: 0.95rem;
  color: $color-text-muted;
  margin: 0;

  &--sm { font-size: 0.88rem; }
}

.onboard-section-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: $color-text;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 0.5rem;
}

.onboard-form-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.onboard-form-row {
  display: grid;
  gap: 0.75rem;

  &--half { grid-template-columns: 1fr 1fr; }
  &--thirds { grid-template-columns: 1fr 1fr 1fr; }
}

.onboard-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  label {
    font-size: 0.82rem;
    font-weight: 600;
    color: $color-text-muted;
  }

  input, textarea, select {
    border: 1.5px solid $color-border;
    border-radius: 0.5rem;
    padding: 0.55rem 0.75rem;
    font-size: 0.95rem;
    color: $color-text;
    background: $color-surface;
    transition: border-color 0.15s;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: $color-primary;
    }
  }

  textarea { resize: vertical; }

  &--psa select { max-width: 320px; }
}

.req { color: $color-danger; }

.onboard-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: $color-text;
  cursor: pointer;

  input[type="checkbox"] { cursor: pointer; }
}

.org-skipped {
  margin: 0.5rem 0 0 1.25rem;
  list-style: disc;
}

.onboard-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.65rem 1rem;
  border-radius: 0.5rem;

  &--ok {
    background: rgba($color-success, 0.1);
    color: $color-success;
  }

  &--err {
    background: rgba($color-danger, 0.1);
    color: $color-danger;
  }
}

.onboard-modal__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  padding-top: 0.5rem;
}

.onboard-btn {
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: background 0.15s, border-color 0.15s;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--primary {
    background: $color-primary;
    color: #fff;
    border-color: $color-primary;

    &:not(:disabled):hover { filter: brightness(1.08); }
  }

  &--secondary {
    background: transparent;
    color: $color-primary;
    border-color: $color-primary;

    &:not(:disabled):hover { background: rgba($color-primary, 0.08); }
  }

  &--ghost {
    background: transparent;
    color: $color-text-muted;
    border-color: $color-border;

    &:not(:disabled):hover {
      background: $color-surface-alt;
      color: $color-text;
    }
  }
}

// ── Contacts list ──
.onboard-contact-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 300px;
  overflow-y: auto;
  border: 1.5px solid $color-border;
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.onboard-contact-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover { background: $color-surface-alt; }

  input[type="checkbox"] { flex-shrink: 0; cursor: pointer; }
}

.contact-name {
  font-weight: 600;
  color: $color-text;
  min-width: 12rem;
}

.contact-email {
  color: $color-text-muted;
  font-size: 0.85rem;
}

// ── Org board ──
.org-board {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1rem;
  min-height: 320px;
}

.org-board__right {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.org-admin-reports {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.org-admin-reports__connector {
  width: 1.5rem;
  flex-shrink: 0;
  align-self: stretch;
  border-left: 2px solid $color-border;
  border-bottom: 2px solid $color-border;
  border-radius: 0 0 0 0.5rem;
  margin-left: 1.25rem;
  margin-top: -2px;
  margin-bottom: 0.5rem;
}

.org-admin-reports > .org-zone--managers {
  flex: 1;
  margin-top: 0.5rem;
}

.org-zone {
  border: 2px dashed $color-border;
  border-radius: 0.75rem;
  padding: 0.75rem;
  min-height: 80px;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: $color-primary;
    background: rgba($color-primary, 0.03);
  }

  &--pool {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    max-height: 420px;
    overflow-y: auto;
  }

  &--admin {
    border-color: rgba($color-warning, 0.5);
  }

  &--managers {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    flex: 1;
  }
}

.org-zone__title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $color-text-muted;
  margin-bottom: 0.4rem;

  &--sm { margin-bottom: 0.25rem; }
}

.org-zone__hint {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.org-zone__hint-drop {
  font-size: 0.82rem;
  color: $color-text-subtle;
  font-style: italic;
  margin: 0;
  text-align: center;
  padding: 0.5rem 0;
}

.org-zone__empty {
  font-size: 0.82rem;
  color: $color-text-subtle;
  font-style: italic;
  margin: 0;
  text-align: center;
  padding: 0.5rem 0;
}

.org-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: $color-surface;
  border: 1.5px solid $color-border;
  border-radius: 0.5rem;
  padding: 0.45rem 0.65rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: grab;
  user-select: none;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:active { cursor: grabbing; }
  &:hover { border-color: $color-primary; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }

  i { color: $color-text-muted; font-size: 1rem; }

  &--admin { border-color: $color-warning; background: rgba($color-warning, 0.06); }
  &--manager { border-color: $color-primary; background: rgba($color-primary, 0.05); }
  &--report { border-left: 3px solid $color-primary; }
}

.org-card__email {
  font-weight: 400;
  font-size: 0.78rem;
  color: $color-text-muted;
  margin-left: auto;
}

.org-card__badge {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $color-primary;
  background: rgba($color-primary, 0.1);
  padding: 0.1rem 0.4rem;
  border-radius: 0.3rem;
}

.org-card__action {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 600;
  color: $color-primary-dark;
  background: transparent;
  border: 1px solid rgba($color-primary, 0.4);
  border-radius: $radius-sm;
  padding: 0.1rem 0.45rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.org-manager {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.org-manager__reports {
  margin-left: 1.25rem;
  border-left: 2px solid $color-border;
  padding-left: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-height: 40px;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  border-radius: 0 0.4rem 0.4rem 0;
  transition: background 0.12s;

  &:hover { background: rgba($color-primary, 0.03); }
}
</style>
